import React, { useEffect, useMemo, useRef, useState } from 'react';
import { XIcon } from 'lucide-react';
import { DscrScenario } from '../types/dscr';
import { formatRatio, formatTimestamp } from '../utils/dscr';
type SaveMode = 'new' | 'overwrite';
interface SaveScenarioModalProps {
  open: boolean;
  defaultName: string;
  dscr: number;
  savedBy: string;
  scenarios: DscrScenario[];
  activeScenarioId: string | null;
  onClose: () => void;
  onSave: (name: string, note: string) => void;
  onOverwrite: (id: string, note: string) => void;
}
export function SaveScenarioModal({
  open,
  defaultName,
  dscr,
  savedBy,
  scenarios,
  activeScenarioId,
  onClose,
  onSave,
  onOverwrite
}: SaveScenarioModalProps) {
  const canOverwrite = scenarios.length > 0;
  const [mode, setMode] = useState<SaveMode>('new');
  const [name, setName] = useState(defaultName);
  const [note, setNote] = useState('');
  const [targetId, setTargetId] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    const initialMode: SaveMode = canOverwrite && activeScenarioId ? 'overwrite' : 'new';
    setMode(initialMode);
    setName(defaultName);
    setNote('');
    setTargetId(activeScenarioId ?? scenarios[0]?.id ?? '');
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open, defaultName, activeScenarioId, canOverwrite, scenarios]);
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  const target = useMemo(() => scenarios.find(scenario => scenario.id === targetId) ?? null, [scenarios, targetId]);
  if (!open) return null;
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (mode === 'overwrite' && target) {
      onOverwrite(target.id, note);
      return;
    }
    onSave(name, note);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-id="element-63" data-mp="3odor23w5p3ym">
      <div role="dialog" aria-modal="true" aria-labelledby="save-scenario-title" className="w-full max-w-md rounded-lg bg-white shadow-xl" data-id="element-64" data-mp="3kcv0f2rbxb21">
        <header className="flex items-start justify-between border-b border-slate-200 px-5 py-4" data-id="element-65" data-mp="3af74i9hzlxia">
          <div data-id="element-66" data-mp="35flzpig4yuxn">
            <h2 id="save-scenario-title" className="text-sm font-semibold text-slate-900" data-id="element-67" data-mp="35g3zgp20brab">
              Save DSCR scenario
            </h2>
            <p className="mt-1 text-xs text-slate-500" data-id="element-68" data-mp="30nuc9gsa15bq">
              Saving as {savedBy} · DSCR {formatRatio(dscr)}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#0176d3]/40" data-id="element-69" data-mp="2roz6g5t59x8y">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-70" data-mp="27mcsz70w5w63" />
          </button>
        </header>

        <form onSubmit={handleSubmit} data-id="element-71" data-mp="2clfnvyn5qm0c">
          <div className="space-y-4 px-5 py-5" data-id="element-72" data-mp="2cked7va201j5">
            <fieldset data-id="element-73" data-mp="2cn8w3idf5eag">
              <legend className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-74" data-mp="2hksbwmprlz4i">
                Save method
              </legend>
              <div className="grid grid-cols-2 gap-2" data-id="element-75" data-mp="2miu35uxch4r2">
                {[{
                value: 'new' as SaveMode,
                label: 'Save as new',
                hint: 'Adds a version'
              }, {
                value: 'overwrite' as SaveMode,
                label: 'Overwrite existing',
                hint: 'Replaces values'
              }].map(option => {
                const disabled = option.value === 'overwrite' && !canOverwrite;
                const selected = mode === option.value;
                return <label key={option.value} className={['flex cursor-pointer flex-col rounded border px-3 py-2.5 text-left transition', selected ? 'border-[#0176d3] bg-[#0176d3]/5 ring-1 ring-[#0176d3]' : 'border-slate-300 hover:bg-slate-50', disabled ? 'cursor-not-allowed opacity-50' : ''].join(' ')} data-id="element-76" data-mp="1b3n9brbgi3ty">
                      <span className="flex items-center gap-2" data-id="element-77" data-mp="3vistl4pq4kxm">
                        <input type="radio" name="save-mode" value={option.value} checked={selected} disabled={disabled} onChange={() => setMode(option.value)} className="h-3.5 w-3.5 accent-[#0176d3]" data-id="element-78" data-mp="3rj1u8cm4swhh" />
                        <span className="text-sm font-semibold text-slate-900" data-id="element-79" data-mp="265z24hsemv3o">
                          {option.label}
                        </span>
                      </span>
                      <span className="mt-1 pl-[22px] text-[11px] text-slate-500" data-id="element-80" data-mp="qlydqyoq23n2">
                        {option.hint}
                      </span>
                    </label>;
              })}
              </div>
            </fieldset>

            {mode === 'new' ? <div data-id="element-81" data-mp="2wna99fzlv58">
                <label htmlFor="scenario-name" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-82" data-mp="2w3zqdso2918">
                  Scenario name
                </label>
                <input id="scenario-name" ref={inputRef} value={name} onChange={event => setName(event.target.value)} className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#0176d3] focus:ring-2 focus:ring-[#0176d3]/30" data-id="element-83" data-mp="3p5ohldqkry2x" />
              </div> : <div data-id="element-84" data-mp="1qs1xgw0bzlbv">
                <label htmlFor="scenario-target" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-85" data-mp="1lus8tikrndp3">
                  Scenario to overwrite
                </label>
                <select id="scenario-target" value={targetId} onChange={event => setTargetId(event.target.value)} className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#0176d3] focus:ring-2 focus:ring-[#0176d3]/30" data-id="element-86" data-mp="1cwgbkxpe9hno">
                  {scenarios.map(scenario => <option key={scenario.id} value={scenario.id} data-id="element-87" data-mp="2sbdqqpi1i6hx">
                      {scenario.name} — DSCR {formatRatio(scenario.dscr)}
                    </option>)}
                </select>
                {target ? <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-id="element-88" data-mp="a32lmohuxa27">
                    Replaces the values saved by {target.savedBy} on{' '}
                    {formatTimestamp(target.savedAt)} (DSCR {formatRatio(target.dscr)} →{' '}
                    {formatRatio(dscr)}). Saved as version {target.version + 1}.
                  </p> : null}
              </div>}

            <div data-id="element-89" data-mp="sz2gc727yrph">
              <label htmlFor="scenario-note" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-90" data-mp="xzq5en6w0yh2">
                Note (optional)
              </label>
              <textarea id="scenario-note" rows={3} value={note} onChange={event => setNote(event.target.value)} placeholder="What changed in this what-if?" className="w-full resize-none rounded border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#0176d3] focus:ring-2 focus:ring-[#0176d3]/30" data-id="element-91" data-mp="16r0btf30cp36" />
            </div>
          </div>

          <footer className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3" data-id="element-92" data-mp="10oa8bgetz6q6">
            <button type="button" onClick={onClose} className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#0176d3]/40" data-id="element-93" data-mp="wm5iksri6u8t">
              Cancel
            </button>
            <button type="submit" disabled={mode === 'overwrite' && !target} className="rounded bg-[#0176d3] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#014486] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#0176d3]/40 focus:ring-offset-2" data-id="element-94" data-mp="hqfr4f3jtowp">
              {mode === 'overwrite' ? 'Overwrite scenario' : 'Save scenario'}
            </button>
          </footer>
        </form>
      </div>
    </div>;
}